import type { INodeDto } from "@/types/INodeDto";
import { NodeTypes } from "@/types/LCAoM/TreeBuilder";
import { ModelTreeNode } from "@/types/ModelTreeNode";
import { CadModelCategory } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useModelNodes } from "@api/models";
import useMultiplier from "@features/analysis/utils/multiplier";
import useAppStore from "@hooks/useAppStore";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, useEffect, useMemo, useState } from "react";

export interface UseModelProps {
  apiRef: MutableRefObject<GridApiPremium>;
  modelId: string | undefined;
  dataVersion?: number;
}

const useModel = (props: UseModelProps) => {
  const { modelId, apiRef, dataVersion } = props;
  const [hasCo2MaterialTypeSpecs, setHasCo2MaterialTypeSpecs] = useState(false);
  const [co2MaterialTypeSpecKind, setCo2MaterialTypeSpecKind] = useState<string | null>(null);
  const appStore = useAppStore();
  const cadModelCategories = useCadModelCategories();
  const modelNodes = useModelNodes(props.modelId, { enabled: !!props.modelId });
  const { applyMultiplierTotals } = useMultiplier();

  const loading = useMemo(() => {
    const isLoading = cadModelCategories.isFetching || modelNodes.isFetching;
    return isLoading;
  }, [cadModelCategories.isFetching, modelNodes.isFetching, modelId]);

  useEffect(() => {
    if (!modelNodes.data || modelNodes.data.length === 0 || !cadModelCategories.data) {
      return;
    }

    const hasCo2MaterialTypeSpec = modelNodes.data.some(n =>
      n.co2MaterialTypeSpec != null && n.co2MaterialTypeSpec.trim() !== ""
    ) || false;
    setHasCo2MaterialTypeSpecs(hasCo2MaterialTypeSpec);

    const nodeWithCo2 = modelNodes.data.find(n =>
      n.co2MaterialTypeSpec != null && n.co2MaterialTypeSpec.trim() !== ""
    );
    let extractedKind: string | null = null;
    if (nodeWithCo2?.co2MaterialTypeSpec) {
      try {
        const parsed = JSON.parse(nodeWithCo2.co2MaterialTypeSpec);
        extractedKind = parsed?.Kind ?? null;
      } catch {
        extractedKind = null;
      }
    }
    setCo2MaterialTypeSpecKind(extractedKind);

    updateAssignNodes();
  }, [modelNodes.data, cadModelCategories.data]);

  const enablePrefix = false;
  const caPrefix = enablePrefix ? "ca:" : "";
  const ePrefix = enablePrefix ? "e:" : "";
  const coPrefix = enablePrefix ? "co:" : "";

  const getParentNodePath = (nodePath: string | null | undefined): string | undefined => {
    if (!nodePath || nodePath === '/') return undefined;
    const trimmed = nodePath.endsWith('/') ? nodePath.slice(0, -1) : nodePath;
    const parts = trimmed.split('/').filter(p => p !== '');
    if (parts.length <= 1) return undefined;
    parts.pop();
    return '/' + parts.join('/') + '/';
  };

  const nodeToModelTreeNode = (node: INodeDto, cadModelCategories: CadModelCategory[]): ModelTreeNode | null => {
    if (!node.nodeId) return null;

    const key = node.nodeId.toString();
    let type: "category" | "group" | "element" | "component" = "element";
    let label = node.cadName || "";
    let leaf = false;

    switch (node.nodeType) {
      case NodeTypes.Category:
        type = "category";
        label = caPrefix + (cadModelCategories.find(cmc => cmc.cadModelCategoryId === node.cadModelCategoryId)?.label || "");
        leaf = (node.componentCount || 0) === 0;
        break;
      case NodeTypes.Family:
        type = "group";
        label = node.cadFamily || "";
        leaf = false;
        break;
      case NodeTypes.Type:
        type = "group";
        label = node.cadName || "";
        leaf = false;
        break;
      case NodeTypes.Element:
        type = "element";
        label = ePrefix + (node.cadName || "");
        leaf = (node.componentCount || 0) === 0;
        break;
      case NodeTypes.Component:
        type = "component";
        label = coPrefix + (node.cadName || "");
        leaf = true;
        break;
      default:
        return null;
    }

    return {
      key,
      type,
      data: {
        ...node,
        nodeId: node.nodeId,
        label,
        siblings: node.componentCount || 1,
        elementId: node.nodeType === NodeTypes.Element ? node.nodeId.toString() : undefined,
        componentId: node.nodeType === NodeTypes.Component ? node.nodeId.toString() : undefined,
        categoryId: node.nodeType === NodeTypes.Category ? node.nodeId.toString() : undefined,
        cadModelCategoryId: node.cadModelCategoryId || null,
        cadFamily: node.cadFamily || null,
        cadName: node.cadName || null,
        cadId: node.cadId || null,
        cadMaterial: node.cadMaterial || null,
        cadReferenceId: node.cadReferenceId || null,
        attribute1: node.freeText1 || "",
        attribute2: node.freeText2 || "",
        attribute3: node.freeText3 || "",
        systemId: node.systemId || "",
        subSystemId: node.subSystemId || "",
        multiplierInput: node.multiplierInput || "",
        multiplierValue: node.multiplierValue ?? null,
        notes: node.notes || "",
        hasChildMultiplierOverride: (node as any).hasChildMultiplierOverride || false,
        carbonImpactInput: node.carbonImpactInput || "",
        carbonImpactValue: node.carbonImpactValue ?? null,
        carbonImpactNotes: node.carbonImpactNotes || "",
        hasChildCarbonImpactOverride: node.hasChildCarbonImpactOverride || false,
        assigned: true,
      },
      children: [],
      leaf,
    };
  };

  const updateAssignNodes = () => {
    if (!modelNodes.data || modelNodes.data.length === 0 || !cadModelCategories.data) {
      return;
    }

    if (!apiRef?.current) {
      return;
    }

    const nodes = modelNodes.data.filter(n => n.nodePath && n.nodeId);

    const nodePathMap = new Map<string, INodeDto>();
    const nodeIdMap = new Map<string, INodeDto>();
    const childrenMap = new Map<string, string[]>();
    const rootNodeIds: string[] = [];

    nodes.forEach(node => {
      const nodeId = node.nodeId!.toString();
      nodeIdMap.set(nodeId, node);
      if (node.nodePath) {
        nodePathMap.set(node.nodePath, node);
      }
    });

    nodes.forEach(node => {
      const nodeId = node.nodeId!.toString();
      if (node.nodePath) {
        const parentPath = getParentNodePath(node.nodePath);
        if (parentPath && nodePathMap.has(parentPath)) {
          const parentId = nodePathMap.get(parentPath)!.nodeId!.toString();
          if (!childrenMap.has(parentId)) {
            childrenMap.set(parentId, []);
          }
          childrenMap.get(parentId)!.push(nodeId);
        } else {
          rootNodeIds.push(nodeId);
        }
      } else {
        rootNodeIds.push(nodeId);
      }
    });

    const rootNodes = rootNodeIds
      .map(nodeId => nodeIdMap.get(nodeId))
      .filter((node): node is INodeDto => node !== undefined && node.nodeType === NodeTypes.Category)
      .map(node => node.nodeId!.toString());

    const buildTreeNode = (nodeId: string): ModelTreeNode | null => {
      const node = nodeIdMap.get(nodeId);
      if (!node) return null;

      const treeNode = nodeToModelTreeNode(node, cadModelCategories.data || []);
      if (!treeNode) return null;

      const children = childrenMap.get(nodeId) || [];
      treeNode.children = children
        .map(childId => buildTreeNode(childId))
        .filter((child): child is ModelTreeNode => child !== null)
        .sort((a, b) => (a.data.label || "").localeCompare(b.data.label || ""));

      treeNode.leaf = treeNode.children.length === 0 && (node.nodeType === NodeTypes.Element ? (node.componentCount || 0) === 0 : node.nodeType === NodeTypes.Component);

      if (treeNode.children.length > 0) {
        treeNode.data.assigned = treeNode.children.every(child => child.data.assigned);
      }

      return treeNode;
    };

    const treeNodes: ModelTreeNode[] = rootNodes
      .map(nodeId => buildTreeNode(nodeId))
      .filter((node): node is ModelTreeNode => node !== null)
      .sort((a, b) => (a.data.label || "").localeCompare(b.data.label || ""));

    const rowNodes = flattenizeTree(treeNodes, []);
    const validNodes = rowNodes?.filter((node) => {
      if (!node || typeof node !== "object") {
        return false;
      }
      if ((node as any).depth === undefined || (node as any).depth === null) {
        return false;
      }
      return true;
    });

    // IMPORTANT: applyMultiplierTotals now only enforces original stored totals into display totals
    const nodesWithMultiplierTotals = applyMultiplierTotals(validNodes);

    appStore.setAssignNodes(nodesWithMultiplierTotals as ModelTreeNode[]);

    const nodesToUpdate = appStore.assignNodes || [];
    const invalidUpdateNodes = nodesToUpdate.filter((node) => !node || (node as any).depth === undefined);

    if (invalidUpdateNodes.length > 0) {
      return;
    }

    apiRef.current.setRows?.(nodesToUpdate);
    apiRef.current?.unstable_applyFilters?.();
  };

  useEffect(() => {
    if (appStore.assignNodes === null && !!cadModelCategories.data && !!modelNodes.data) {
      updateAssignNodes();
    }
  }, [appStore.assignNodesRecalc, cadModelCategories.data, modelNodes.data]);

  const join = (value: string | undefined) => {
    if (value) return value + "/";
    return "";
  };

  const appendPath = (path: string[], key: string | number | undefined) => {
    if (key) path.push(key.toString());
    return path;
  };

  const flattenizeTree = (nodes: ModelTreeNode[] | undefined, path: string[]) => {
    let rows: any[] = [];

    if (nodes) {
      nodes.forEach((node: ModelTreeNode, index: number) => {
        path = appendPath(path, `${index}`);
        node.path = [...path];
        node.depth = path.length - 1;
        rows.push(node);

        const childNodes = flattenizeTree(node.children, path);
        if (childNodes.length > 0) {
          rows = rows.concat(childNodes);
        }

        path.pop();
      });
    }
    return rows;
  };

  return { loading, hasCo2MaterialTypeSpecs, co2MaterialTypeSpecKind };
};

export default useModel;
